export interface ReportOption {
  id: string;
  label: string;
  value: string;
}

export interface ReportCategory {
  id: string;
  name: string;
  description: string;
  options: ReportOption[];
  allowNA?: boolean;
}

export const REPORT_CATEGORIES: ReportCategory[] = [
  {
    id: "timing",
    name: "Timing",
    description: "How punctual was the activity?",
    options: [
      { id: "timing_on_time", label: "On time", value: "on_time" },
      { id: "timing_slightly_early", label: "Slightly early", value: "slightly_early" },
      { id: "timing_slightly_late", label: "Slightly late (<15 min)", value: "slightly_late" },
      { id: "timing_significantly_late", label: "Significantly late (>15 min)", value: "significantly_late" },
      { id: "timing_client_late", label: "Client was late", value: "client_late" },
    ],
  },
  {
    id: "engagement",
    name: "Quality of Engagement",
    description: "How engaged were the clients?",
    options: [
      { id: "engagement_exceptional", label: "Exceptional", value: "exceptional" },
      { id: "engagement_good", label: "Good", value: "good" },
      { id: "engagement_adequate", label: "Adequate", value: "adequate" },
      { id: "engagement_below", label: "Below expectations", value: "below_expectations" },
      { id: "engagement_poor", label: "Poor", value: "poor" },
    ],
  },
  {
    id: "transport",
    name: "Transport Quality",
    description: "Quality of transport (if applicable)",
    allowNA: true,
    options: [
      { id: "transport_excellent", label: "Excellent", value: "excellent" },
      { id: "transport_good", label: "Good", value: "good" },
      { id: "transport_acceptable", label: "Acceptable", value: "acceptable" },
      { id: "transport_poor", label: "Poor", value: "poor" },
    ],
  },
  {
    id: "satisfaction",
    name: "Client Satisfaction",
    description: "Perceived satisfaction of the clients",
    options: [
      { id: "satisfaction_very_satisfied", label: "Very satisfied", value: "very_satisfied" },
      { id: "satisfaction_satisfied", label: "Satisfied", value: "satisfied" },
      { id: "satisfaction_neutral", label: "Neutral", value: "neutral" },
      { id: "satisfaction_dissatisfied", label: "Dissatisfied", value: "dissatisfied" },
      { id: "satisfaction_very_dissatisfied", label: "Very dissatisfied", value: "very_dissatisfied" },
    ],
  },
  {
    id: "guide_rating",
    name: "Guide's Overall Rating",
    description: "The guide's overall experience with this activity",
    options: [
      { id: "guide_excellent", label: "Excellent experience", value: "excellent" },
      { id: "guide_good", label: "Good experience", value: "good" },
      { id: "guide_average", label: "Average experience", value: "average" },
      { id: "guide_challenging", label: "Challenging experience", value: "challenging" },
      { id: "guide_would_not_repeat", label: "Would not repeat", value: "would_not_repeat" },
    ],
  },
  {
    id: "issues",
    name: "Issues Arising",
    description: "Any issues that arose during the activity",
    options: [
      { id: "issues_none", label: "None", value: "none" },
      { id: "issues_minor_resolved", label: "Minor issue (resolved)", value: "minor_resolved" },
      { id: "issues_ongoing", label: "Ongoing issue logged", value: "ongoing" },
      { id: "issues_critical", label: "Critical issue (requires follow-up)", value: "critical" },
    ],
  },
];

// Helper to get a category by ID
export const getCategoryById = (categoryId: string): ReportCategory | undefined => {
  return REPORT_CATEGORIES.find((cat) => cat.id === categoryId);
};

// Helper to get option label by category and value
export const getOptionLabel = (categoryId: string, value: string): string => {
  const category = getCategoryById(categoryId);
  if (!category) return value;
  const option = category.options.find((opt) => opt.value === value);
  return option?.label || value;
};
